import Fuse from "fuse.js";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;

const FUSE_OPTIONS = {
  keys: [
    { name: "question", weight: 2 },
    { name: "answer", weight: 1 },
  ],
  threshold: 0.3,
  ignoreLocation: true,
  minMatchCharLength: MIN_QUERY_LENGTH,
};

class FaqHeader extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("[data-search-input]");
    this.jumplinks = this.querySelector("[data-jumplinks]");
    this.resultsCount = this.querySelector("[data-results-count]");
    this.results = this.querySelector("[data-results]");

    if (!this.input || !this.results) return;

    this.fuse = null;
    this.query = "";

    this.onInput = this._debounce(() => this.search(this.input.value.trim()));
    this.invalidateIndex = () => {
      this.fuse = null;
    };

    this.input.addEventListener("input", this.onInput);
    // FAQ Category sections can be added, removed or edited in the theme editor.
    document.addEventListener("shopify:section:load", this.invalidateIndex);
    document.addEventListener("shopify:section:unload", this.invalidateIndex);
  }

  disconnectedCallback() {
    this.input?.removeEventListener("input", this.onInput);
    document.removeEventListener("shopify:section:load", this.invalidateIndex);
    document.removeEventListener(
      "shopify:section:unload",
      this.invalidateIndex,
    );
  }

  search(query) {
    if (query === this.query) return;
    this.query = query;

    if (query.length < MIN_QUERY_LENGTH) {
      this.reset();
      return;
    }

    const matches = this._index()
      .search(query)
      .map(({ item }) => item.element);

    this.render(query, matches);
  }

  render(query, matches) {
    this.results.replaceChildren(
      ...matches.map((element) => this._cloneClosed(element)),
    );

    if (matches.length) {
      this.resultsCount.textContent = this._countLabel(matches.length);
    } else {
      this.resultsCount.textContent = this._label("no-results").replace(
        "[query]",
        query,
      );
    }

    this.results.classList.toggle("hidden", matches.length === 0);
    this.jumplinks?.classList.add("hidden");
    this._categories().forEach((category) => category.classList.add("hidden"));
  }

  reset() {
    this.results.replaceChildren();
    this.results.classList.add("hidden");
    this.resultsCount.textContent = "";
    this.jumplinks?.classList.remove("hidden");
    this._categories().forEach((category) =>
      category.classList.remove("hidden"),
    );
  }

  /**
   * Results reuse the FAQ Category markup so they pick up the same styles and
   * get a fresh `conditional-details` upgrade when appended.
   */
  _cloneClosed(element) {
    const clone = element.cloneNode(true);
    const details = clone.querySelector("details");

    details?.classList.remove("open");
    details?.removeAttribute("open");
    clone.querySelector("[data-content]")?.removeAttribute("style");

    return clone;
  }

  _index() {
    if (this.fuse) return this.fuse;

    const items = [
      ...document.querySelectorAll("[data-faq-category] [data-faq-item]"),
    ].map((element) => ({
      element,
      question: element.dataset.question || "",
      answer: element.dataset.answer || "",
    }));

    this.fuse = new Fuse(items, FUSE_OPTIONS);
    return this.fuse;
  }

  _categories() {
    return document.querySelectorAll("[data-faq-category]");
  }

  _countLabel(count) {
    const key = count === 1 ? "count-singular" : "count-plural";
    return this._label(key).replace("[count]", count);
  }

  _label(name) {
    return this.getAttribute(name) || "";
  }

  _debounce(fn) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), DEBOUNCE_MS);
    };
  }
}

if (!customElements.get("faq-header")) {
  customElements.define("faq-header", FaqHeader);
}
